/**
 * main.js
 * App entry point. Wires inputs → calculator → UI.
 * Business logic lives in modules, not here.
 */

import { calculate, formatDollar, barPct } from './modules/calculator.js'
import {
  isPro,
  getFilamentPresets,
  saveFilamentPreset,
  getPrinterProfiles,
  savePrinterProfile,
  getHistory,
  saveToHistory,
} from './modules/presets.js'
import { exportPDF } from './modules/pdf.js'
import { validateStoredLicense, activateLicense } from './modules/license.js'

// ---------------------------------------------------------------------------
// DOM refs
// ---------------------------------------------------------------------------
const $ = id => document.getElementById(id)
const esc = s => {
  const d = document.createElement('div')
  d.textContent = s
  return d.innerHTML
}

const inputs = {
  filCost:    $('filCost'),
  grams:      $('grams'),
  hours:      $('hours'),
  watts:      $('watts'),
  elecRate:   $('elecRate'),
  margin:     $('margin'),
  overhead:   $('overhead'),
  advToggle:  $('advToggle'),
  failRate:   $('failRate'),
  laborHrs:   $('laborHrs'),
  laborRate:  $('laborRate'),
}

// ---------------------------------------------------------------------------
// Read current input values
// ---------------------------------------------------------------------------
function readInputs() {
  return {
    filCost:     parseFloat(inputs.filCost.value)    || 0,
    grams:       parseFloat(inputs.grams.value)      || 0,
    hours:       parseFloat(inputs.hours.value)      || 0,
    watts:       parseFloat(inputs.watts.value)      || 0,
    elecRate:    parseFloat(inputs.elecRate.value)   || 0,
    marginPct:   parseFloat(inputs.margin.value)     || 0,
    overheadPct: parseFloat(inputs.overhead.value)   || 0,
    failRate:    inputs.advToggle.checked
                   ? (parseFloat(inputs.failRate.value) || 0)
                   : 0,
    laborHrs:    inputs.advToggle.checked
                   ? (parseFloat(inputs.laborHrs.value) || 0)
                   : 0,
    laborRate:   inputs.advToggle.checked
                   ? (parseFloat(inputs.laborRate.value) || 0)
                   : 0,
  }
}

// ---------------------------------------------------------------------------
// Render results to DOM
// ---------------------------------------------------------------------------
function renderResults(result) {
  const {
    matCost, elecCost, laborCost, failBuffer, overheadAmt,
    totalCost, profitAmt, salePrice, marginPct, overheadPct
  } = result

  $('rMat').textContent    = formatDollar(matCost)
  $('rElec').textContent   = formatDollar(elecCost)
  $('rTotal').textContent  = formatDollar(totalCost)
  $('rPrice').textContent  = formatDollar(salePrice)
  $('rBreakEven').textContent = `Break-even: ${formatDollar(totalCost)}`
  $('priceNote').textContent  = `at ${marginPct}% margin · ${overheadPct}% overhead`

  const adv = inputs.advToggle.checked
  $('rTotalSub').textContent = (adv && failBuffer > 0)
    ? `incl. ${inputs.failRate.value}% fail buffer`
    : ''

  // Bars
  const setPct = (el, val) => { el.style.width = barPct(val, salePrice) }
  setPct($('bMat'),    matCost);     $('bMatAmt').textContent    = formatDollar(matCost)
  setPct($('bElec'),   elecCost);    $('bElecAmt').textContent   = formatDollar(elecCost)
  setPct($('bOver'),   overheadAmt); $('bOverAmt').textContent   = formatDollar(overheadAmt)
  setPct($('bProfit'), profitAmt);   $('bProfitAmt').textContent = formatDollar(profitAmt)

  const laborTotal = laborCost + failBuffer
  $('laborRow').style.display = (adv && laborTotal > 0) ? 'flex' : 'none'
  if (adv) {
    setPct($('bLabor'), laborTotal)
    $('bLaborAmt').textContent = formatDollar(laborTotal)
  }
}

// ---------------------------------------------------------------------------
// Main calculate and render cycle
// ---------------------------------------------------------------------------
let lastInputs = null
let lastResult = null

function run() {
  lastInputs = readInputs()
  lastResult = calculate(lastInputs)
  renderResults(lastResult)
}

// ---------------------------------------------------------------------------
// Slider display sync
// ---------------------------------------------------------------------------
inputs.margin.addEventListener('input', () => {
  $('marginVal').textContent = inputs.margin.value + '%'
  run()
})

inputs.overhead.addEventListener('input', () => {
  $('overheadVal').textContent = inputs.overhead.value + '%'
  run()
})

// ---------------------------------------------------------------------------
// Advanced toggle
// ---------------------------------------------------------------------------
inputs.advToggle.addEventListener('change', () => {
  const adv = $('advInputs')
  adv.classList.toggle('hidden', !inputs.advToggle.checked)
  adv.classList.toggle('visible', inputs.advToggle.checked)
  run()
})

// ---------------------------------------------------------------------------
// Number inputs
// ---------------------------------------------------------------------------
;['filCost','grams','hours','watts','elecRate',
  'failRate','laborHrs','laborRate'].forEach(id => {
  $(id).addEventListener('input', run)
})

// ---------------------------------------------------------------------------
// PDF export
// ---------------------------------------------------------------------------
$('exportBtn').addEventListener('click', async () => {
  if (!isPro() || !lastResult) return

  const label = prompt('Job name (optional):', 'Print Job') ?? 'Print Job'

  await exportPDF(lastInputs, lastResult, label)

  saveToHistory(label, lastInputs, lastResult)
  renderHistory()
})

// ---------------------------------------------------------------------------
// Save filament preset
// ---------------------------------------------------------------------------
$('savePresetBtn').addEventListener('click', () => {
  if (!isPro()) return

  const name = prompt('Filament name (e.g. Bambu PLA Basic):')
  if (!name) return

  const brand    = prompt('Brand:') ?? ''
  const material = prompt('Material type (PLA, PETG, ABS...):') ?? ''

  saveFilamentPreset({
    name,
    brand,
    material,
    costPerKg: lastInputs?.filCost ?? 0
  })

  renderPresets()
})

// ---------------------------------------------------------------------------
// Save printer profile
// ---------------------------------------------------------------------------
$('savePrinterBtn').addEventListener('click', () => {
  if (!isPro()) return

  const name = prompt('Printer name (e.g. Bambu X1C):')
  if (!name) return

  savePrinterProfile({
    name,
    watts:    lastInputs?.watts    ?? 0,
    elecRate: lastInputs?.elecRate ?? 0
  })

  renderPrinters()
})

// ---------------------------------------------------------------------------
// Render preset lists
// ---------------------------------------------------------------------------
function renderPresets() {
  const list    = $('presetList')
  const presets = getFilamentPresets()

  if (!isPro()) {
    list.innerHTML = '<div class="preset-empty">Save filament presets with Pro.<br>Reload cost in one click.</div>'
    return
  }

  if (presets.length === 0) {
    list.innerHTML = '<div class="preset-empty">No presets yet. Save your first filament below.</div>'
    return
  }

  list.innerHTML = presets.map(p => `
    <div class="preset-item" data-id="${esc(p.id)}">
      <div class="preset-name">${esc(p.name)}</div>
      <div class="preset-meta">$${esc(String(p.costPerKg))}/kg · ${esc(p.material)}</div>
      <button class="preset-load" data-cost="${esc(String(p.costPerKg))}">Load</button>
    </div>
  `).join('')

  list.querySelectorAll('.preset-load').forEach(btn => {
    btn.addEventListener('click', () => {
      inputs.filCost.value = btn.dataset.cost
      run()
    })
  })
}

function renderPrinters() {
  const list     = $('printerList')
  const profiles = getPrinterProfiles()

  if (!isPro()) {
    list.innerHTML = '<div class="preset-empty">Save printer profiles with Pro.<br>Load wattage and rate instantly.</div>'
    return
  }

  if (profiles.length === 0) {
    list.innerHTML = '<div class="preset-empty">No profiles yet. Save your first printer below.</div>'
    return
  }

  list.innerHTML = profiles.map(p => `
    <div class="preset-item" data-id="${esc(p.id)}">
      <div class="preset-name">${esc(p.name)}</div>
      <div class="preset-meta">${esc(String(p.watts))}W · $${esc(String(p.elecRate))}/kWh</div>
      <button class="preset-load" data-watts="${esc(String(p.watts))}" data-rate="${esc(String(p.elecRate))}">Load</button>
    </div>
  `).join('')

  list.querySelectorAll('.preset-load').forEach(btn => {
    btn.addEventListener('click', () => {
      inputs.watts.value    = btn.dataset.watts
      inputs.elecRate.value = btn.dataset.rate
      run()
    })
  })
}

function renderHistory() {
  const history = getHistory()
  const section = $('historySection')
  const list    = $('historyList')

  if (!isPro() || history.length === 0) {
    section.style.display = 'none'
    return
  }

  section.style.display = 'block'

  list.innerHTML = history.map(entry => `
    <div class="history-item">
      <div class="history-label">${esc(entry.label)}</div>
      <div class="history-meta">
        ${esc(new Date(entry.savedAt).toLocaleDateString())}
      </div>
      <div class="history-price">
        ${esc(formatDollar(entry.result.salePrice))}
      </div>
    </div>
  `).join('')
}

// ---------------------------------------------------------------------------
// Pro UI gate
// ---------------------------------------------------------------------------
function applyProState() {
  const pro = isPro()

  $('planBadge').textContent = pro ? 'Pro' : 'Free'
  $('planBadge').classList.toggle('badge-pro',  pro)
  $('planBadge').classList.toggle('badge-free', !pro)

  $('upgradeBtn').style.display = pro ? 'none' : 'inline'

  $('exportBtn').disabled      = !pro
  $('savePresetBtn').disabled  = !pro
  $('savePrinterBtn').disabled = !pro
  $('exportNote').style.display = pro ? 'none' : 'inline'

  renderPresets()
  renderPrinters()
  renderHistory()
}

// ---------------------------------------------------------------------------
// Upgrade button
// ---------------------------------------------------------------------------
$('upgradeBtn').addEventListener('click', async () => {
  if (isPro()) return

  const action = confirm(
    'Enter a license key to activate Pro features.\n\n' +
    'Click OK to enter a key, or Cancel to go to the purchase page.'
  )

  if (!action) {
    window.open('https://spoolledger.lemonsqueezy.com/checkout/buy/9a0d3298-25bd-47ed-864c-fb2b52e2299d', '_blank')
    return
  }

  const key = prompt('Paste your license key:')
  if (!key) return

  const result = await activateLicense(key.trim())

  if (result.ok) {
    alert('Pro activated! Enjoy saved presets and PDF export.')
    applyProState()
  } else {
    alert('Activation failed: ' + result.error)
  }
})

// --------------------------------------------------------------
// Waitlist form
// --------------------------------------------------------------
const FORMSPREE_URL = 'https://formspree.io/f/xwvwaoyz'

// Capture UTM params from the URL into the hidden form fields.
// Runs once at load; values stay put until submission.
const urlParams = new URLSearchParams(window.location.search)
const sourceField = $('waitlistSource')
const campaignField = $('waitlistCampaign')

if (sourceField && urlParams.get('utm_source')) {
  sourceField.value = urlParams.get('utm_source')
}
if (campaignField && urlParams.get('utm_campaign')) {
  campaignField.value = urlParams.get('utm_campaign')
}

$('waitlistForm').addEventListener('submit', async (e) => {
  e.preventDefault()

  $('waitlistEmail').setCustomValidity('')
  const email = $('waitlistEmail').value.trim()
  if (!email) return

  const source = sourceField ? sourceField.value : 'direct'
  const campaign = campaignField ? campaignField.value : 'none'

  try {
    const res = await fetch(FORMSPREE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, source, campaign }),
    })

    if (res.ok) {
      $('waitlistForm').classList.add('hidden')
      $('waitlistConfirm').classList.remove('hidden')
    } else {
      $('waitlistEmail').setCustomValidity('Something went wrong — please try again.')
      $('waitlistEmail').reportValidity()
    }
  } catch {
    $('waitlistEmail').setCustomValidity('Network error — please try again.')
    $('waitlistEmail').reportValidity()
  }
})

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
async function init() {
  await validateStoredLicense()
  applyProState()
  run()
}

init()